// Qualify once per visit, then wait for a quiet moment outside shopping and support flows.
(function () {
  'use strict';
  if (window.__cqSurveyReadinessStarted) return;
  const host = window.location.hostname;
  if (/^(localhost|127(?:\.\d+){3}|0\.0\.0\.0|\[?::1\]?)$/.test(host) || host.endsWith('.localhost') || window.Shopify?.theme?.role === 'development') return;
  window.__cqSurveyReadinessStarted = true;

  const pagesKey = 'cq_survey_pages';
  const sentKey = 'cq_survey_ready_sent';
  const activeKey = 'cq_survey_active_ms';
  const minimumVisible = 60000, minimumIdle = 8000;
  let storage, pages, activeMs;
  try {
    storage = window.sessionStorage;
    pages = (parseInt(storage.getItem(pagesKey) || '0', 10) || 0) + 1;
    activeMs = Number(storage.getItem(activeKey) || 0);
    if (!Number.isFinite(activeMs) || activeMs < 0) activeMs = 0;
    storage.setItem(pagesKey, String(pages));
    if (storage.getItem(sentKey)) return;
  } catch (_) { return; } // Without visit storage, fail closed rather than repeatedly asking.

  const clock = () => window.performance.now();
  let stopped = false, timer = null, visible = !document.hidden;
  let anchor = clock(), lastActivity = anchor;
  let surveysReady = false, loadingSurveys = false, loadAttempts = 0;
  const listeners = [];
  function listen(target, name, handler) {
    target.addEventListener(name, handler, {capture:true, passive:true});
    listeners.push([target, name, handler]);
  }
  function stop() {
    stopped = true;
    if (timer !== null) clearTimeout(timer);
    timer = null;
    listeners.forEach(([target, name, handler]) => target.removeEventListener(name, handler, true));
  }
  function sent() {
    if (stopped) return true;
    try { return Boolean(storage.getItem(sentKey)); }
    catch (_) { stop(); return true; }
  }
  function refreshVisit() {
    if (sent()) { stop(); return false; }
    try {
      // Restored documents can receive visibilitychange before pageshow. Merge before every write.
      pages = Math.max(pages, parseInt(storage.getItem(pagesKey) || '0', 10) || 0);
      const savedMs = Number(storage.getItem(activeKey) || 0);
      if (Number.isFinite(savedMs)) activeMs = Math.max(activeMs, savedMs);
      return true;
    } catch (_) { stop(); return false; }
  }
  function checkpoint() {
    if (!refreshVisit()) return false;
    const now = clock();
    // Persist elapsed visible time, never a timestamp that would count a navigation/background gap.
    if (visible) activeMs += Math.max(0, now - anchor);
    anchor = now;
    try { storage.setItem(activeKey, String(activeMs)); return true; }
    catch (_) { stop(); return false; }
  }
  function isVisible(element) {
    if (element.closest('[hidden], [inert], [aria-hidden="true"]') || !element.getClientRects().length) return false;
    return getComputedStyle(element).visibility !== 'hidden';
  }
  function blocked() {
    const path = window.location.pathname;
    if (/(?:^|\/)(?:cart|checkouts?|contact(?:-[^/]*)?|(?:create-)?support-ticket|submit-photos-support)(?:\/|$)/i.test(path)) return true;
    // Never on the homepage, and never once something is in the cart: a survey there interrupts a shopper who has just
    // added a system. The side cart's state carries the live item count on every page.
    if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/?)?$/i.test(path)) return true;
    if (Array.from(document.querySelectorAll('[data-cq-cart-state]')).some(state => Number(state.dataset.count) > 0)) return true;
    const focused = document.activeElement;
    // Cross-origin forms expose only the iframe as focused; do not interrupt their input either.
    if (focused?.matches('input, textarea, select, iframe') || focused?.closest('[contenteditable]:not([contenteditable="false"])')) return true;
    if (document.body?.matches('.cq-cart-open, .header-mini-cart-open, .drawer-open')) return true;
    const overlays = document.querySelectorAll('dialog[open], modal-dialog[open], product-modal[open], product-compare-modal[open], [role="dialog"][open], [aria-modal="true"], cq-cart-drawer[aria-hidden="false"], quick-view-drawer[aria-hidden="false"]');
    if (Array.from(overlays).some(isVisible)) return true;
    // Quiz questions and loading screens need uninterrupted input; landing/result pages may qualify.
    return Array.from(document.querySelectorAll('[data-cqq-quiz]:not(.is-landing):not(.is-result)')).some(isVisible);
  }
  function schedule(delay = 5000) {
    if (!stopped && visible && timer === null) timer = setTimeout(attempt, delay);
  }
  function attempt() {
    timer = null;
    if (sent()) { stop(); return; }
    if (!checkpoint()) return;
    if (!visible || document.hidden) return;
    try {
      if (pages < 3 || activeMs < minimumVisible || clock() - lastActivity < minimumIdle || blocked()) { schedule(); return; }
      const ph = window.posthog;
      // Do not poll the SDK or load survey definitions until timing and placement are both suitable.
      if (!surveysReady) {
        if (++loadAttempts > 40) { stop(); return; }
        if (ph?.__loaded && !loadingSurveys) {
          loadingSurveys = true;
          const loaded = () => { if (!stopped) surveysReady = true; };
          if (typeof ph.onSurveysLoaded === 'function') ph.onSurveysLoaded(loaded);
          else if (typeof ph.getSurveys === 'function') ph.getSurveys(loaded);
          else { stop(); return; }
        }
        schedule(1000);
        return;
      }
      // The checks above also run after async survey loading, immediately before readiness is emitted.
      storage.setItem(sentKey, '1');
      try { ph.capture('cq_survey_ready', {reason:'pages'}); }
      catch (_) { try { storage.removeItem(sentKey); } catch (_) {} }
      stop();
    } catch (_) { stop(); }
  }
  function activity() { lastActivity = clock(); }
  function visibilityChanged() {
    if (!checkpoint()) return;
    visible = !document.hidden;
    anchor = clock();
    lastActivity = anchor;
    if (timer !== null) clearTimeout(timer);
    timer = null;
    schedule();
  }
  function pageHidden() {
    checkpoint();
    visible = false;
    if (timer !== null) clearTimeout(timer);
    timer = null;
  }
  function pageShown() {
    if (stopped || !checkpoint()) return;
    visible = !document.hidden;
    anchor = clock();
    lastActivity = anchor;
    schedule();
  }
  ['pointerdown', 'pointermove', 'keydown', 'input', 'scroll', 'focusin', 'focusout'].forEach(name => listen(document, name, activity));
  listen(document, 'visibilitychange', visibilityChanged);
  listen(window, 'pagehide', pageHidden);
  listen(window, 'pageshow', pageShown);
  schedule();
})();
